import { io, Socket } from 'socket.io-client';

/** Invoked when the server acknowledges this emit (Socket.IO callback / ack). */
export type RealTimeSendAck = (...responseArgs: unknown[]) => void;

// This is not used anywhere
export enum RealTimeEvents {
	ACTIVITY_CHANGE = 'activity change',
	WAY_POINT_ARRIVED = 'way point arrived',
	WAY_POINT_DONE = 'way point done',
	WAY_POINT_ETA_UPDATE = 'way point eta updated',
	WAY_POINT_LOCATION_UPDATE = 'way point location updated',
	ORDER_DONE = 'order done',
	ORDER_CANCELED = 'order cancelled',
	LOCATION_UPDATE = 'location update',
	WATCH_ORDER = 'watch order',
	WATCH_DRIVER = 'watch driver',
	WATCH_WAY_POINT = 'watch way point',
	CONNECT = 'connect'
}

export default class RealTimeService {
	_socket: Socket;
	realTimeUrl: string;
	socketWebsocketPort: number;
	securedSockets: boolean;
	private _onVisibilityChange: () => void;

	constructor(realTimeUrl: string, socketWebsocketPort?: number, securedSockets?: boolean) {
		this.realTimeUrl = realTimeUrl;
		this.socketWebsocketPort = socketWebsocketPort ? socketWebsocketPort : 443;
		this.securedSockets = securedSockets ? securedSockets : true;
		this._onVisibilityChange = () => this.reconnectIfNeeded();
	}

	init() {
		if (this._socket) {
			this._socket.disconnect();
		}

		this._socket = io(this.realTimeUrl, {
			transports: ['websocket'],
			transportOptions: {
				websocket: {
					port: this.socketWebsocketPort,
					secure: this.securedSockets
				}
			}
		});

		if (typeof document !== 'undefined') {
			document.addEventListener('visibilitychange', this._onVisibilityChange);
		}
	}

	private reconnectIfNeeded() {
		if (typeof document === 'undefined' || document.visibilityState !== 'visible' || !this._socket) {
			return;
		}
		if (!this._socket.connected) {
			this._socket.connect();
		}
	}

	/**
	 * Emit a realtime event (same semantics as {@link Socket.emit}).
	 * When provided, `ack` is invoked after the server runs the acknowledgement for this emit.
	 * @see https://socket.io/docs/v4/client-api/#socketemiteventname-args
	 */
	send(eventName: string): void;
	send(eventName: string, payload: unknown, ack?: RealTimeSendAck): void;
	send(eventName: string, ...args: any[]): void {
		this._socket.emit(eventName, ...args);
	}

	listen(eventName: string, fn: Function) {
		//@ts-ignore Type of evnet listener is more complex in socket, and it's complex to-reuse this type here
		this._socket.on(eventName, fn);
	}

	removeEvent(eventName: string, eventFunction?: (...args: any[]) => void) {
		this._socket.off(eventName, eventFunction);
	}

	removeAllEvents() {
		if (typeof document !== 'undefined') {
			document.removeEventListener('visibilitychange', this._onVisibilityChange);
		}
		// @ts-ignore This method exist on component-emitter the types are incorrect
		this._socket.removeAllListeners();
	}
}
